import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import { ROLE_LABEL } from '@/constants/roles';
import type { DayKind } from '@/types/staffAttendance';
import type { StaffLeaveStatus, StaffLeaveType } from '@/types/staffLeave';
import type { EmploymentType, MaritalStatus, SalaryPaymentMode, StaffStatus } from '@/types/staff';
import { STAFF_TYPES } from './schemas/staff.schema';

/**
 * How the staff screen describes an account.
 *
 * The type labels are read from the shared `ROLE_LABEL` rather than written out
 * again — "Accountant" appears in three places already, and a second spelling of
 * it is a bug waiting for somebody to notice (CLAUDE.md §3).
 */
export const STAFF_TYPE_OPTIONS: Option[] = STAFF_TYPES.map((value) => ({
  value,
  label: ROLE_LABEL[value],
}));

/**
 * The role filter's value for "everybody who does not teach". Not an account
 * type, so the page sends it as the `nonTeaching` flag rather than `staffType`.
 */
export const NON_TEACHING_FILTER = 'NON_TEACHING';

/**
 * The staff list's role filter: every type an institute can create, and the
 * administrator account the list also shows — which nobody can create here,
 * so it is not in `STAFF_TYPE_OPTIONS`.
 */
export const STAFF_ROLE_FILTER_OPTIONS: Option[] = [
  ...STAFF_TYPE_OPTIONS,
  { value: 'ADMIN', label: 'Administration' },
  { value: NON_TEACHING_FILTER, label: 'Non-teaching staff' },
];

/** What each kind of account is for, under the picker. */
export const STAFF_TYPE_HINT: Record<(typeof STAFF_TYPES)[number], string> = {
  PRINCIPAL: 'Oversees the institute.',
  TEACHER: 'Classroom staff — teaches, marks and reports.',
  ACCOUNTANT: 'Fees, receipts and the institute’s accounts.',
  LIBRARIAN: 'Runs the library.',
  COUNSELLOR: 'Student wellbeing and counselling.',
  DRIVER: 'Drives a school vehicle. Signs in to the driver app with their mobile number.',
};

export const STAFF_STATUS_LABEL = {
  ACTIVE: 'Active',
  /** Suspended from this console. Same word as the lockout's status — both block sign-in. */
  INACTIVE: 'Suspended',
  /** Set by the login lockout, and by suspending an account. */
  SUSPENDED: 'Suspended',
} as const satisfies Record<StaffStatus, string>;

export const STAFF_STATUS_TONE = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  SUSPENDED: 'critical',
} as const satisfies Record<StaffStatus, BadgeTone>;

/** Only the two a person may choose. `SUSPENDED` is the lockout's to set. */
export const STAFF_STATUS_OPTIONS: Option[] = [
  { value: 'ACTIVE', label: STAFF_STATUS_LABEL.ACTIVE },
  { value: 'INACTIVE', label: STAFF_STATUS_LABEL.INACTIVE },
];

/**
 * The filter row's choices. Suspending from this console writes `INACTIVE`, so
 * one "switched off" option covers it and the lockout's `SUSPENDED` — the
 * server matches both, as the summary card counts both.
 */
export const STAFF_STATUS_FILTER_OPTIONS: Option[] = [
  { value: 'ACTIVE', label: STAFF_STATUS_LABEL.ACTIVE },
  { value: 'INACTIVE', label: STAFF_STATUS_LABEL.INACTIVE },
];

export const MARITAL_STATUS_LABEL = {
  SINGLE: 'Single',
  MARRIED: 'Married',
  SEPARATED: 'Separated',
  DIVORCED: 'Divorced',
  WIDOWED: 'Widowed',
} as const satisfies Record<MaritalStatus, string>;

export const MARITAL_STATUS_OPTIONS: Option[] = Object.entries(MARITAL_STATUS_LABEL).map(
  ([value, label]) => ({ value, label }),
);

export const EMPLOYMENT_TYPE_LABEL = {
  FULL_TIME: 'Full time',
  PART_TIME: 'Part time',
  CONTRACT: 'Contract',
  VISITING: 'Visiting',
} as const satisfies Record<EmploymentType, string>;

export const EMPLOYMENT_TYPE_OPTIONS: Option[] = Object.entries(EMPLOYMENT_TYPE_LABEL).map(
  ([value, label]) => ({ value, label }),
);

/** How a salary goes out — on the enrolment form and on each recorded payment. */
export const SALARY_PAYMENT_MODE_LABEL = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
} as const satisfies Record<SalaryPaymentMode, string>;

export const SALARY_PAYMENT_MODE_OPTIONS: Option[] = Object.entries(SALARY_PAYMENT_MODE_LABEL).map(
  ([value, label]) => ({ value, label }),
);

/** Rows per page. One value, so the table and the server never disagree. */
export const STAFF_PER_PAGE = 20;

/**
 * How the profile describes a day on the attendance calendar.
 *
 * One table rather than a colour decided at each of the places a kind appears —
 * the log row, the summary tile and the legend (CLAUDE.md §3).
 */
export const ATTENDANCE_DAY_LABEL: Record<DayKind, string> = {
  PRESENT: 'Present',
  PENDING: 'Awaiting check',
  REJECTED: 'Not accepted',
  ABSENT: 'Absent',
  LEAVE: 'On leave',
  WEEK_OFF: 'Week off',
  HOLIDAY: 'Holiday',
  UPCOMING: 'Not yet',
};

/** The pill each kind wears in the daily log. */
export const ATTENDANCE_DAY_TONE: Record<DayKind, string> = {
  PRESENT:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  REJECTED: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  ABSENT: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  LEAVE: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400',
  WEEK_OFF: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  HOLIDAY: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400',
  UPCOMING: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
};

/** The institute's own words for each kind of leave, and what it is for. */
export const STAFF_LEAVE_LABEL: Record<StaffLeaveType, string> = {
  CASUAL: 'Casual leave (CL)',
  SICK: 'Sick / medical leave (SL)',
  PRIVILEGE: 'Privilege / annual paid leave (PL)',
};

export const STAFF_LEAVE_HINT: Record<StaffLeaveType, string> = {
  CASUAL: 'Short-term routine personal leave',
  SICK: 'Health, or a doctor’s appointment',
  PRIVILEGE: 'Accumulated annual paid leave',
};

/** The bar each kind fills on its balance card. */
export const STAFF_LEAVE_BAR: Record<StaffLeaveType, string> = {
  CASUAL: 'bg-indigo-600',
  SICK: 'bg-amber-500',
  PRIVILEGE: 'bg-emerald-500',
};

export const STAFF_LEAVE_TYPE_OPTIONS: Option[] = (
  Object.keys(STAFF_LEAVE_LABEL) as StaffLeaveType[]
).map((value) => ({ value, label: STAFF_LEAVE_LABEL[value] }));

export const STAFF_LEAVE_STATUS_LABEL: Record<StaffLeaveStatus, string> = {
  PENDING: 'Awaiting decision',
  APPROVED: 'Approved',
  REJECTED: 'Refused',
  CANCELLED: 'Withdrawn',
};

export const STAFF_LEAVE_STATUS_TONE: Record<StaffLeaveStatus, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'critical',
  CANCELLED: 'neutral',
};
